import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {RagAttachmentDTO} from 'crqlar-react-common/dist/models/ai/ragChat.model';
import {CloudinaryService} from 'crqlar-react-common/dist/services/cloudinary.service';
import {fileNameFromMarkdownImageUrl} from 'crqlar-react-common/dist/utils/markdownImage.util';

export interface PendingScreenshot {
    id: string;
    blob?: Blob;
    file?: File;
    url: string;
    remoteUrl?: string;
    fileName?: string;
    mimeType?: string;
    metadata?: Record<string, any>;
    revokeUrl?: boolean;
}

const createScreenshotId = () =>
    `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const revokeScreenshotUrls = (screenshots: PendingScreenshot[]) => {
    screenshots.forEach(screenshot => {
        if (screenshot.revokeUrl) URL.revokeObjectURL(screenshot.url);
    });
};

const uploadPendingScreenshot = async (screenshot: PendingScreenshot): Promise<RagAttachmentDTO> => {
    if (screenshot.remoteUrl) {
        return {
            type: 'IMAGE',
            url: screenshot.remoteUrl,
            mimeType: screenshot.mimeType,
            fileName: screenshot.fileName || fileNameFromMarkdownImageUrl(screenshot.remoteUrl),
            metadata: {
                ...screenshot.metadata,
                source: 'url',
            },
        } as RagAttachmentDTO;
    }

    const file = screenshot.file || new File(
        [screenshot.blob!],
        screenshot.fileName || `screenshot-${screenshot.id}.png`,
        {type: screenshot.mimeType || screenshot.blob?.type || 'image/png'},
    );
    const uploaded = await CloudinaryService.upload(file);
    const url = uploaded?.secure_url || uploaded?.url;
    if (!url) throw new Error('No URL in Cloudinary response');

    return {
        type: 'IMAGE',
        url,
        storageKey: uploaded?.public_id,
        mimeType: file.type || screenshot.mimeType || 'image/png',
        fileName: file.name,
        metadata: {
            bytes: file.size,
            width: uploaded?.width,
            height: uploaded?.height,
        },
    } as RagAttachmentDTO;
};

export const usePendingScreenshots = () => {
    const [items, setItems] = useState<PendingScreenshot[]>([]);
    const [previewId, setPreviewId] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const itemsRef = useRef<PendingScreenshot[]>([]);

    const setScreenshots = useCallback((
        updater: PendingScreenshot[] | ((current: PendingScreenshot[]) => PendingScreenshot[]),
    ) => {
        setItems(current => {
            const next = typeof updater === 'function' ? updater(current) : updater;
            itemsRef.current = next;
            return next;
        });
    }, []);

    useEffect(() => () => {
        revokeScreenshotUrls(itemsRef.current);
    }, []);

    const addOrReplace = useCallback((blob: Blob, replacingId?: string) => {
        const url = URL.createObjectURL(blob);
        setScreenshots(current => {
            if (!replacingId) {
                return [...current, {
                    id: createScreenshotId(),
                    blob,
                    url,
                    mimeType: blob.type || 'image/png',
                    revokeUrl: true,
                }];
            }

            let replaced = false;
            const next = current.map(screenshot => {
                if (screenshot.id !== replacingId) return screenshot;
                if (screenshot.revokeUrl) URL.revokeObjectURL(screenshot.url);
                replaced = true;
                return {
                    id: screenshot.id,
                    blob,
                    url,
                    mimeType: blob.type || 'image/png',
                    revokeUrl: true,
                };
            });

            if (!replaced) {
                URL.revokeObjectURL(url);
                return current;
            }

            return next;
        });
    }, [setScreenshots]);

    const addFile = useCallback((file: File) => {
        const url = URL.createObjectURL(file);
        setScreenshots(current => [
            ...current,
            {
                id: createScreenshotId(),
                file,
                blob: file,
                url,
                fileName: file.name,
                mimeType: file.type,
                revokeUrl: true,
            },
        ]);
    }, [setScreenshots]);

    const addImageUrl = useCallback((url: string) => {
        setScreenshots(current => [
            ...current,
            {
                id: createScreenshotId(),
                url,
                remoteUrl: url,
                fileName: fileNameFromMarkdownImageUrl(url),
                metadata: {source: 'url'},
            },
        ]);
    }, [setScreenshots]);

    const remove = useCallback((id: string) => {
        setScreenshots(current => {
            const target = current.find(screenshot => screenshot.id === id);
            if (target?.revokeUrl) URL.revokeObjectURL(target.url);
            return current.filter(screenshot => screenshot.id !== id);
        });
        setPreviewId(current => (current === id ? null : current));
    }, [setScreenshots]);

    const clear = useCallback(() => {
        revokeScreenshotUrls(itemsRef.current);
        itemsRef.current = [];
        setItems([]);
        setPreviewId(null);
    }, []);

    const upload = useCallback(async (): Promise<RagAttachmentDTO[] | undefined> => {
        const screenshots = itemsRef.current;
        if (screenshots.length === 0) return undefined;

        setUploading(true);
        try {
            return await Promise.all(screenshots.map(uploadPendingScreenshot));
        } finally {
            setUploading(false);
        }
    }, []);

    const preview = useMemo(
        () => previewId ? items.find(screenshot => screenshot.id === previewId) ?? null : null,
        [items, previewId],
    );

    return {
        items,
        preview,
        uploading,
        addOrReplace,
        addFile,
        addImageUrl,
        remove,
        clear,
        upload,
        setPreviewId,
    };
};
